import type { CategoryContent } from './row.js';

export const DRAWING_PAPER: CategoryContent = {
  bscba: [
    [
      'What graphite pencil grade indicates the hardest standard drawing pencil?',
      [
        '9H',
        '9B',
        'HB',
        '2B',
      ],
      0,
      'The H scale (hard) runs from H to 9H; B scale (black/soft) runs from B to 9B. 9H is the hardest standard grade, leaving faint marks.',
    ],
    [
      'In printmaking, what is the defining characteristic of a relief print?',
      [
        'Carving away material leaves raised, ink-receiving surfaces',
        'Ink is applied to a polished stone surface via a chemical process',
        'The image is etched into metal using acid',
        'Ink sits in grooves cut into a printing plate',
      ],
      0,
      'Relief printing (woodcut, linocut) works by carving away unwanted areas, leaving the raised surfaces to be inked and printed.',
    ],
    [
      'Which medium is created by drawing or scratching on a prepared metallic surface, typically copper?',
      [
        'Etching',
        'Lithography',
        'Engraving',
        'Woodcut',
      ],
      0,
      'Etching uses acid to bite into metal through a wax-covered plate; the artist scratches away the protective layer to create lines.',
    ],
    [
      'What is the primary difference between chalk and soft pastel as drawing media?',
      [
        'Pastel has a binder; chalk is unbound pigment',
        'Chalk is more vibrant in color than pastel',
        'Pastel cannot be blended on paper',
        'Chalk is used only for underpainting',
      ],
      0,
      'Pastel contains a binder (gum arabic or oil) that holds pigment together; chalk is unbounded mineral or pigment powder.',
    ],
    [
      'In silverpoint drawing, what surface is required to work on?',
      [
        'Paper prepared with a white ground',
        'Raw paper, prepared the same as for graphite',
        'A waxed metal plate',
        'A primed wooden panel',
      ],
      0,
      'Silverpoint requires a prepared ground layer; the silver point leaves tiny metal particles that adhere to the abrasive surface.',
    ],
    [
      'Which printmaking technique uses a flat stone or metal plate where ink-receptive and ink-repellent areas coexist?',
      [
        'Lithography',
        'Engraving',
        'Aquatint',
        'Woodcut',
      ],
      0,
      'Lithography exploits the chemical principle that grease and water do not mix; oil-based ink adheres to greasy drawn areas and repels wet areas.',
    ],
    [
      'What is watercolor in the context of drawing?',
      [
        'Pigment bound in gum arabic and dissolved in water',
        'A type of graphite pencil mixed with water',
        'An opaque paint used exclusively for finished works',
        'A wet drawing technique using ink on unsized paper',
      ],
      0,
      'Watercolor is pigment suspended in a gum arabic binder; it is applied wet and dries transparent, allowing paper to show through.',
    ],
    [
      'What does the term "preparatory drawing" or "study" typically mean in art history?',
      [
        'A sketch made before the final composition',
        'A finished drawing intended for exhibition',
        'A copy of another artist\'s work for educational purposes',
        'A drawing made after a sculpture is completed',
      ],
      0,
      'Preparatory drawings are working sketches—compositional studies, figure studies, or detail explorations done before the finished work.',
    ],
    [
      'In intaglio printmaking, what is the primary distinction between etching and engraving?',
      [
        'Etching uses acid; engraving cuts lines by hand',
        'Engraving produces deeper lines than etching',
        'Etching can only be done on copper; engraving only on steel',
        'Engraving requires acid; etching does not',
      ],
      0,
      'Etching employs acid to etch lines through a resist layer; engraving cuts lines mechanically with a burr or burin tool.',
    ],
    [
      'What is the purpose of fixative spray applied to charcoal or pastel drawings?',
      [
        'To bind pigment and prevent smudging',
        'To darken the colors and increase saturation',
        'To make the drawing water-resistant',
        'To permanently set the paper to a wooden backing',
      ],
      0,
      'Fixative is a thin adhesive coating that locks loose particles of charcoal or pastel in place, reducing smudging.',
    ],
    [
      'Which of these is a hand tool used to apply or blend drawing media?',
      [
        'A blending stump',
        'A deckle',
        'A burr',
        'A scraper',
      ],
      0,
      'A blending stump is a tightly rolled paper tool used to blend and smooth charcoal, pastel, or graphite on paper.',
    ],
    [
      'What does "connoisseurship" mean in the context of drawing collections?',
      [
        'Expert judgment in authenticating artworks',
        'The process of framing drawings for display',
        'A formal degree in art history',
        'The restoration and cleaning of old drawings',
      ],
      0,
      'Connoisseurship is the informed, expert eye developed through long study—the ability to attribute, judge condition, and assess authenticity and quality.',
    ],
    [
      'In what medium did Leonardo da Vinci create many of his famous anatomical and compositional studies?',
      [
        'Silverpoint and chalk on prepared paper',
        'Ink and watercolor only',
        'Graphite pencil (in the modern sense)',
        'Charcoal alone',
      ],
      0,
      'Leonardo\'s studies combine silverpoint, black chalk, red chalk, and pen-and-ink—media standard to Renaissance drawing practice.',
    ],
    [
      'What is the main advantage of using India ink in pen-and-ink drawing?',
      [
        'It is permanent and waterproof',
        'It is easily erasable and allows for corrections',
        'It creates a glossy finish when dry',
        'It can be diluted with water to create color gradations',
      ],
      0,
      'India ink is valued for permanence and waterproof quality; once dry, it resists smudging and degradation.',
    ],
    [
      'Which printmaking technique involves carving an image into the end grain of a wooden block?',
      [
        'Wood engraving',
        'Lithography',
        'Etching',
        'Linocut',
      ],
      0,
      'Wood engraving uses the hard end grain of boxwood; traditional woodcut uses the plank grain. End-grain allows finer detail.',
    ],
  ],
  msc: [
    [
      'What is the significance of the "white ground" technique used in Old Master drawings, particularly during the Northern Renaissance?',
      [
        'It created a stable, neutral mid-tone base',
        'It prevented silver oxidation in silverpoint drawings',
        'It was purely decorative and did not affect the drawing process',
        'It allowed transparent watercolor to be applied over ink',
      ],
      0,
      'A white ground (often bone meal, lead white, or chalk) provided a stable, controllable base that allowed Old Masters to exploit lights, darks, and middle tones in silverpoint and chalk studies.',
    ],
    [
      'In etching, what technical effect does creating a deeper bite produce?',
      [
        'Darker, richer lines from more retained ink',
        'Lighter lines, as deeper grooves hold less ink',
        'A glossy finish on the finished print',
        'The ability to print in multiple colors from one plate',
      ],
      0,
      'Deeper acid bite creates wider, deeper grooves; these grooves hold more ink during wiping, resulting in darker, richer printed lines.',
    ],
    [
      'Which Old Master artist is particularly renowned for extensive figure studies and anatomical drawings in chalk?',
      [
        'Michelangelo',
        'Rembrandt',
        'Claude Lorrain',
        'Albrecht Dürer',
      ],
      0,
      'Michelangelo\'s chalk and charcoal studies—especially his figure and muscle studies—are canonical Old Master works demonstrating mastery of form.',
    ],
    [
      'What conservation challenge is specific to silverpoint drawings over long periods?',
      [
        'The silver oxidizes and tarnishes over time',
        'The prepared ground absorbs moisture and becomes unstable',
        'The silver migrates into the paper fibers, causing brittleness',
        'Silverpoint drawings invariably fade unless kept in complete darkness',
      ],
      0,
      'Silverpoint oxidizes naturally; tarnishing darkens the gray lines, which is why Old Master silverpoints often appear darker than when first drawn.',
    ],
    [
      'In lithography, what is the chemical principle behind the grease-resist process?',
      [
        'Grease attracts oil ink; water repels it',
        'The limestone surface converts grease into a waterproof seal',
        'Oil-based ink bonds permanently to grease while water washes away other particles',
        'Grease penetrates the stone, hardening its surface',
      ],
      0,
      'Lithography relies on the hydrophobic-oleophilic principle: greasy drawn areas attract oil-based ink; wet areas (covered with water) repel it.',
    ],
    [
      'What role did preparatory drawings play in Renaissance workshop practice?',
      [
        'They served compositional, transfer, and training roles',
        'They were considered inferior and discarded after the final work was completed',
        'They served only as personal sketches never shared or valued',
        'They were mandatory preliminary stages that all artists had to complete before painting',
      ],
      0,
      'Renaissance workshops treated drawings as compositional blueprints, teaching tools, and records of artistic development—integral to the creative process.',
    ],
    [
      'How does aquatint differ from traditional etching in technical execution?',
      [
        'Fine resin dust creates speckled tonal areas',
        'Aquatint uses a liquid resist instead of a wax ground',
        'Aquatint is a surface technique requiring no acid bath',
        'Aquatint can only be used on steel plates, not copper',
      ],
      0,
      'Aquatint employs granulated resin to create tonal texture; acid bites between particles, producing a speckled tone rather than fine lines.',
    ],
    [
      'What is the primary difference between a collector\'s drawing and a museum-quality drawing in terms of condition and market assessment?',
      [
        'Museum drawings meet stricter conservation standards',
        'Collector drawings are always more valuable than museum drawings',
        'Museum-quality drawings are newer and therefore more desirable',
        'Collector-grade drawings cannot be insured or sold',
      ],
      0,
      'Museum standards prioritize original condition, stable materials, and reversible conservation; collector pieces may tolerate more intervention and loss.',
    ],
    [
      'In Old Master drawing authentication, what does "attributional ambiguity" typically indicate?',
      [
        'Scholarly uncertainty about who created a work',
        'A deliberate forgery that experts have agreed to reject',
        'A work that is entirely unsigned and therefore worthless',
        'A drawing copied so skillfully that the original artist is unknowable',
      ],
      0,
      'Attributional ambiguity reflects the complexity of Old Master scholarship—shared stylistic conventions, workshop collaboration, and incomplete provenance create legitimate scholarly debate.',
    ],
    [
      'What technical advantage did drypoint offer to Renaissance and later engravers?',
      [
        'It produced a rich, velvety line without acid',
        'It was faster than etching and required no chemical expertise',
        'It could produce an unlimited number of prints from a single plate',
        'It allowed artists to work on softer metals than traditional engraving',
      ],
      0,
      'Drypoint uses a burr (raised metal edge) created by drawing with a needle; the burr holds extra ink, producing characteristically dark, fuzzy lines.',
    ],
    [
      'What is the significance of the "underdrawing" visible in many Old Master paintings when examined under infrared reflectography?',
      [
        'It reveals the artist\'s hidden compositional changes',
        'It indicates that the painting was a copy of an earlier work',
        'It proves that the artist used a stencil for accuracy',
        'It shows where conservation restorers have made repairs',
      ],
      0,
      'Underdrawing (graphite, chalk, or ink sketches beneath paint) reveals artistic process, changes of mind, and compositional development—crucial for attribution and understanding technique.',
    ],
    [
      'Why do pastel drawings require special archival storage and mounting techniques?',
      [
        'Pastel pigment is loosely bound and can shift or flake',
        'Pastel is chemically reactive with most mounting materials',
        'Pastel cannot tolerate humidity above 30%',
        'Pastel drawings spontaneously fade without UV protection',
      ],
      0,
      'Pastel particles are held only by surface friction and binder; pressure, vibration, and friction cause pigment loss and blending, requiring protective matting and flat storage.',
    ],
    [
      'In connoisseurship, what is a "pentimento" and why is it significant in authentication?',
      [
        'A visible artist\'s correction during creation',
        'A signature or monogram hidden in a drawing',
        'A preparation technique where chalk is rubbed into paper',
        'A chemical marker used to date old drawings',
      ],
      0,
      'Pentimenti (plural; singular pentimento) are artist corrections visible in drawings—overpainting, erasing, or revising lines. They often indicate authentic hand-drawn work.',
    ],
    [
      'What does the term "state" mean when referring to an engraving or etching?',
      [
        'Each version of a print after plate changes',
        'The condition of a printed sheet when it arrives from the printmaker',
        'The country or region where a print was created',
        'The number of colors used in the print',
      ],
      0,
      'A "state" is a specific version of a print plate; artists often modify the plate (adding or removing lines, deepening acid bites), creating different states with distinct visual differences.',
    ],
    [
      'How do scholars use X-ray fluorescence (XRF) spectroscopy to analyze Old Master drawings?',
      [
        'It identifies ink and pigment composition',
        'It measures the thickness of paper fibers to determine origin',
        'It detects forged signatures through ink analysis',
        'It removes harmful conservation treatments from drawings',
      ],
      0,
      'XRF is a non-invasive analytical tool that identifies metallic and elemental content of media—revealing ink composition, pigment origin, and sometimes anachronistic materials in suspect works.',
    ],
  ],
  phd: [
    [
      'What structural property of Renaissance chalk grounds, particularly regarding particle size distribution, affected the tonal range achievable in Old Master drawings?',
      [
        'Finer grounds allowed more subtle mid-tone modeling',
        'Coarser grounds absorbed more water and were preferred for watercolor overlay',
        'Particle size had no documented effect on drawing technique',
        'Finer grounds were used only for underpainting and rejected for finished work',
      ],
      0,
      'Ground composition directly influenced drawing behavior: finer bone meal allowed delicate layering and subtle modeling; coarser grounds resisted fine line definition.',
    ],
    [
      'In Rembrandt\'s etching practice, what technical innovation did his adoption of "surface tone" printing represent?',
      [
        'Uneven wiping left ink residue for tonal variation',
        'Using a second etching needle to create tonal hatching',
        'Applying multiple chalk grounds before etching',
        'Printing on dampened paper to create tonal gradations',
      ],
      0,
      'Rembrandt\'s surface tone technique left ink film on plate areas during wiping, creating rich, painterly tones—a radical departure from traditional clean-wiped intaglio printing.',
    ],
    [
      'How does the degradation pathway of iron gall ink differ from modern synthetic inks in conservation chemistry?',
      [
        'Iron gall ink acidifies and embrittles paper over time',
        'Iron gall ink never fades while modern inks degrade quickly',
        'Both inks degrade identically and require identical conservation treatment',
        'Modern inks are always more fugitive and require darker storage conditions',
      ],
      0,
      'Iron gall ink undergoes complex oxidation producing sulfuric acid that hydrolyzes paper cellulose; modern inks avoid this but may fade photochemically, requiring different conservation strategies.',
    ],
    [
      'What is the significance of the "wet-on-wet" technique in Old Master watercolor, and how does it relate to paper sizing?',
      [
        'Lightly sized paper lets pigment diffuse and soften edges',
        'Wet-on-wet requires heavy sizing to prevent paper deterioration',
        'Sizing prevents wet-on-wet technique and was deliberately avoided',
        'Paper sizing has no effect on watercolor diffusion or edge quality',
      ],
      0,
      'Paper sizing directly controls water absorption: minimal sizing allows pigment diffusion and soft transitions; heavy sizing causes pigment to sit on the surface, creating harder edges.',
    ],
    [
      'In printmaking scholarship, what does the term "cancellation proof" reveal about print editions and artist intent?',
      [
        'A print made after the plate was deliberately cancelled',
        'A proofing stage before the final edition was printed',
        'A mistake in the edition that was rejected by the artist',
        'A print created during the artist\'s lifetime to verify quality control',
      ],
      0,
      'Cancellation proofs are final prints from a destroyed or altered plate, historically used to definitively close an edition and prove its limitation—crucial for provenance and edition documentation.',
    ],
    [
      'How did 18th-century chalk drawing technique evolve in response to improvements in papermaking and the availability of synthetic pigments?',
      [
        'Better paper and pigments enabled bolder, larger works',
        'Synthetic pigments made chalk drawing obsolete',
        'Papermaking innovations had no effect on drawing technique or pigment selection',
        'Artists abandoned chalk in favor of graphite after synthetic pigments were invented',
      ],
      0,
      'Improved paper consistency and synthetic chalk/pastel colors enabled larger, more coloristically ambitious works than earlier drawings were practicable—technological change drove stylistic evolution.',
    ],
    [
      'What is the theoretical framework for "hands" analysis in Old Master drawing authentication, and what are its limitations?',
      [
        'Mark-making analysis suggests authorship, imperfectly',
        'Hands analysis is a definitive scientific method with no room for error',
        'Only graphite or ink drawings can be analyzed by hands authentication',
        'Modern forensic technology has made connoisseurship obsolete',
      ],
      0,
      'Hands analysis relies on detailed observation of mark-making and stylistic quirks, but workshops produced collaborative works, artists evolved, and skilled pupils could imitate closely—hence "attribution," not proof.',
    ],
    [
      'In silverpoint oxidation research, what has infrared reflectography revealed about the visual and material changes in Old Master silverpoint over 500+ years?',
      [
        'Tarnish penetrates the paper, not just the surface',
        'Silverpoint never oxidizes if stored properly',
        'Tarnish can be completely reversed by modern conservation methods',
        'Oxidation occurs only in the presence of sulfur compounds and is entirely preventable',
      ],
      0,
      'IRR studies show that Old Master silverpoint darkening results from both visible surface tarnish and subsurface oxidation; tarnish is chemically persistent and affects the drawing\'s visual character.',
    ],
    [
      'How does the concept of "printmaking as drawing" challenge traditional definitions, particularly in the context of monotype?',
      [
        'Monotype blurs printing and drawing into one image',
        'Monotype is purely a printmaking process with no relationship to drawing',
        'Monotype requires the same repeated pulling as traditional edition prints',
        'Monotype drawings were invented in the 20th century',
      ],
      0,
      'Monotype is a hybrid medium: the plate is the drawing surface, yet the impression is the artwork. Only one or two prints result, making each monotype nearly as unique as a hand-drawn work.',
    ],
    [
      'What role did the anatomical drawing studies of Michelangelo play in Renaissance theory of disegno, and how did this influence printmaking illustration?',
      [
        'His prints made anatomy central to art training',
        'Michelangelo had no influence on printmaking practice',
        'Anatomical studies were considered irrelevant to printmaking',
        'Renaissance artists avoided anatomy in their published works',
      ],
      0,
      'Michelangelo\'s anatomical studies (and their dissemination via prints) made anatomy central to artistic education and printmaking instruction—drawing became the foundation of all visual arts.',
    ],
    [
      'In conservation science, what is the "browning reaction" in iron gall ink, and how does it relate to lignin content in paper?',
      [
        'Iron sulfate catalyzes lignin oxidation in paper',
        'Browning is purely a surface phenomenon unrelated to paper chemistry',
        'Only modern papers experience browning; old papers are resistant',
        'Browning occurs only when drawings are exposed to light',
      ],
      0,
      'Paper browning from iron gall ink involves acid catalysis of lignin oxidation. Low-lignin (acid-free) papers resist this; high-lignin papers (common in older drawings) suffer systematic darkening and embrittlement.',
    ],
    [
      'What does the term "reproductive engraving" mean in art-historical context, and why did its decline after 1870 reshape the market for original drawings?',
      [
        'Photography replaced these mass-copied prints',
        'Reproductive engravings were the main source of artistic income',
        'Photography made original drawings less valuable',
        'Reproductive engravings are still the dominant printmaking technique',
      ],
      0,
      'Mass-production methods obsoleted reproductive engravings by ~1900, paradoxically increasing collector interest in original drawings and prints. The shift from reproduction to original art drove modern collecting culture.',
    ],
    [
      'How do scholarly debates about Michelangelo\'s red chalk drawing technique relate to debates about the accessibility of red chalk supply in Florence during the 1510s?',
      [
        'Chalk scarcity informs attribution theories',
        'Red chalk supply had no effect on drawing practice',
        'All Old Masters used red chalk equally',
        'Michelangelo exclusively used black chalk',
      ],
      0,
      'Red chalk rarity and expense in Renaissance Florence make its use significant for attributional studies—high-cost material suggests direct master execution, economically constraining workshop assistance.',
    ],
    [
      'In printmaking history, what technical advances in steel-facing technology in the 19th century transformed the production economics of intaglio prints?',
      [
        'Steel-faced plates survived far more impressions',
        'Steel facing was invented only in the 20th century',
        'Steel facing weakened copper plates',
        'Steel facing had no effect on print production',
      ],
      0,
      'Steel facing (electroplating a thin steel layer onto copper plates) extended plate life from ~100 impressions to thousands, enabling commercially viable large editions and dramatically lower print costs.',
    ],
    [
      'How have recent provenance research methodologies (including archival cross-referencing and market databases) improved attribution confidence in drawings with unclear ownership histories?',
      [
        'Digital databases help reconstruct ownership chains',
        'Provenance research has made attribution less reliable',
        'Digital tools cannot improve attribution certainty',
        'Archival research is irrelevant to authentication',
      ],
      0,
      'Modern provenance databases (sales catalogs, collection inventories, digitized archives) enable researchers to trace ownership histories and detect forged pedigrees—significantly strengthening attribution confidence.',
    ],
  ],
  professor: [
    [
      'In what ways has recent nanotechnology-based analysis of Old Master paper and media altered the understanding of manufacturing processes and trade networks for drawing materials before 1600?',
      [
        'It reveals sourcing, mill practices, and trade routes',
        'Nanotechnology has not contributed to art historical understanding',
        'Old Master papers and inks cannot be analyzed by modern technology',
        'Trade networks for art materials were well-documented and required no new research',
      ],
      0,
      'Advanced microscopy and elemental analysis reveal fiber composition, papermaking water sources, and ink origins—enabling scholars to reconstruct material supply chains, workshop procurement, and geographic artistic networks.',
    ],
    [
      'What recent scholarship debates the attributional stability of Rembrandt\'s later etchings, particularly regarding the role of studio assistants in plate preparation and printing?',
      [
        'Scholars increasingly question single-artist attribution',
        'Rembrandt working practices were completely understood decades ago',
        'Studio assistants played no role in Rembrandt\'s printmaking output',
        'Modern scholarship has consensus on all Rembrandt etchings\' authenticity',
      ],
      0,
      'Contemporary scholarship recognizes that Rembrandt\'s workshop involved collaborative revision of plates, assistants\' contributions to printing, and multiple hands—complicating traditional "Rembrandt" attribution categories.',
    ],
    [
      'How have conservation studies of the Cranach workshop\'s use of translucent paint layers in preparatory drawings on tinted paper informed understanding of "underpainting-as-finished-work" in Northern Renaissance practice?',
      [
        'Cranach exploited tinted grounds and transparent media',
        'Cranach used traditional Old Master techniques',
        'Tinted paper had no effect on Cranach\'s technique',
        'Conservation studies cannot inform art historical understanding',
      ],
      0,
      'Cranach\'s workshop deliberately used tinted grounds and transparent glazes in drawings-as-finished-works, blurring painting-drawing distinctions and revealing sophisticated understanding of optical effects—challenging traditional medium categories.',
    ],
    [
      'In what ways does the recent re-examination of Leonardo\'s mirror-script writings on drawing pages challenge assumptions about left-handed drawing technique and compositional development?',
      [
        'Pressure and hatching patterns suggest mixed-handedness',
        'Leonardo\'s handedness is well-established with no remaining questions',
        'Mirror script contains no information about artistic technique',
        'Leonardo\'s drawing methods are fully understood',
      ],
      0,
      'Analysis of mirror-script pressure patterns, hatching direction, and spatial flow suggests Leonardo\'s compositional process was more complex than simple left-handed drawing—possibly deliberate spatial encoding.',
    ],
    [
      'How have recent studies of Japanese woodcut technique and Hokusai\'s collaborative workshop practices revised Western understanding of "authorship" in Old Master prints?',
      [
        'Carvers, printers, and designers shared authorship',
        'Japanese prints had no influence on Western art history',
        'Hokusai\'s technique is irrelevant to understanding Western prints',
        'Authorship concepts have remained stable across all cultures and periods',
      ],
      0,
      'Japanese printmaking involved distinct specialists (artist, carver, printer, publisher), challenging Western assumptions about individual authorship—revealing that even signed works involved substantive collaborative labor.',
    ],
    [
      'What does contemporary material culture research on the market for Old Master drawings (1900–1945) reveal about the role of aesthetic ideology in canonizing certain types of drawings over others?',
      [
        'Collectors\' taste for "pure drawing" shaped the canon',
        'Historical aesthetics had no effect on which drawings were valued',
        'All Old Master drawings have always been equally valued',
        'Market forces are irrelevant to art historical canon formation',
      ],
      0,
      'Early modernist collecting privileged finished Old Master drawings and devalued preparatory work; this market-driven canonization persists institutionally, obscuring the historical value of studies and alternative drawing traditions.',
    ],
    [
      'In conservation ethics, how have recent debates about the reversibility of past interventions in Old Master drawings reshaped approaches to treatment decisions?',
      [
        'Irreversible treatments push toward minimal intervention',
        'All conservation methods are equally reversible',
        'Conservation reversibility is not an ethical concern',
        'Modern conservation is always superior to historical treatments',
      ],
      0,
      'Realization that past conservation adhesives, coatings, and mounts cannot be safely removed has catalyzed ethics debates about prevention vs. cure—privileging minimal intervention over aggressive historical treatments.',
    ],
    [
      'What does the emerging field of "digital drawing authentication" using spectral imaging and machine learning reveal about the limitations of human connoisseurship in high-stakes attributions?',
      [
        'Algorithms reveal patterns humans miss',
        'Digital methods cannot improve attribution accuracy',
        'Connoisseurship has always been infallible',
        'Machine learning cannot analyze artistic style',
      ],
      0,
      'Spectral imaging and AI pattern analysis detect micro-variations in mark-making invisible to human connoisseurs, sometimes revealing forgers or workshop hands—demonstrating that traditional eye-based attribution has blind spots.',
    ],
    [
      'How have archival revelations about 18th-century printmakers\' workshop contracts and piece-rate systems altered understanding of creative agency and economic coercion in historical print production?',
      [
        'Contracts reveal harsh patron control behind "genius"',
        'Economic history is irrelevant to understanding print authorship',
        'All artists in history enjoyed complete creative freedom',
        'Workshop contracts tell us nothing about actual creative practice',
      ],
      0,
      'Historical contracts document strict patron control, piece-rate limitations, and design coercion—revealing that many celebrated prints were constrained commissions, not free creative expression.',
    ],
    [
      'What recent scholarship on the chemical degradation of watercolor pigments, particularly synthetic aniline dyes in 19th-century drawings, challenges conservation assumptions about UV light exposure?',
      [
        'Ozone, not just light, degrades aniline dyes',
        'Watercolor pigments never degrade in dark storage',
        'UV light is the only cause of pigment fading',
        'Synthetic dyes are more stable than natural pigments',
      ],
      0,
      'Research shows aniline dye degradation results partly from atmospheric ozone and SO₂ oxidation—not just light. This shifts conservation priorities from light-control alone toward comprehensive environmental management.',
    ],
    [
      'In how does the recent discovery of hidden inscriptions (detected via infrared and X-radiography) in Michelangelo\'s preparatory drawings reframe interpretations of his working methods and studio accountability?',
      [
        'Hidden notes reveal workshop organization',
        'Michelangelo\'s drawings contain no hidden information',
        'Infrared imaging has no value for art historical study',
        'Hidden inscriptions are merely accidental marks',
      ],
      0,
      'IRR reveals Michelangelo\'s notes on technique, cost tracking, and apprentice assignments—evidence of deliberate pedagogical method and workshop systematization, not spontaneous genius.',
    ],
    [
      'What does current provenance scholarship on drawings looted during WWII reveal about the persistent institutional reluctance to repatriate or acknowledge works with incomplete ownership histories?',
      [
        'Databases show holders resisting restitution',
        'All looted artworks have been successfully returned',
        'Provenance research cannot identify stolen drawings',
        'Institutional reluctance to repatriate is not a current issue',
      ],
      0,
      'WWII provenance databases document extensive drawings theft; however, institutional resistance to full accountability and restitution persists, revealing ongoing tension between historical integrity and legal/financial interests.',
    ],
    [
      'How have recent studies of the Dürer workshop\'s print matrices (woodblocks and copper plates) using non-destructive 3D scanning altered understanding of plate revision, reuse, and collaborative authorship?',
      [
        '3D mapping reveals decades of plate revision',
        'Dürer\'s plates were never reused or modified',
        '3D scanning cannot provide information about print matrices',
        'All Dürer prints were created without revision',
      ],
      0,
      '3D matrix analysis reveals systematic revision, reworking, and reuse spanning decades—evidence of collaborative iterative practice and ongoing workshop engagement, not one-time creation.',
    ],
    [
      'What recent theoretical work in visual culture studies challenges the Western "drawing as transcription of vision" metaphor by comparing it to Chinese literati drawing traditions?',
      [
        'Cahill: literati drawing expresses ideas, not likeness',
        'All drawing traditions share identical purposes',
        'Western and Chinese drawing are not comparable',
        'Cultural studies are irrelevant to art historical analysis',
      ],
      0,
      'Chinese literati drawing aims at philosophical and conceptual expression, not optical fidelity—revealing that "drawing as vision transcription" is a culturally specific Western assumption, not a universal category.',
    ],
    [
      'In print conservation ethics, how has the emergence of "reversible alteration" as a standard challenged older conservation decisions that prioritized aesthetic "improvement" over historical accuracy?',
      [
        'New standards challenge past aggressive fixes',
        'All conservation decisions are ethically equivalent',
        'Historical conservation was always optimal',
        'Reversibility is not a conservation concern',
      ],
      0,
      'Reversibility ethics challenges mid-century aggressive cleaning and "improvement"—revealing that aesthetic enhancement often destroys historical authenticity. Modern conservation privileges preservation of evidence over restoration of beauty.',
    ],
  ],
};
